import themeSchema from "./schema";
import { Theme } from "./types";

interface ThemeModule {
  default: Theme;
}

const validateTheme = (theme: Theme): boolean => {
  const { error } = themeSchema.validate(theme);
  if (error) {
    console.error("Theme validation failed:", error);
    return false;
  }
  return true;
};

// Map theme names to file imports
const themeMap: Record<string, () => Promise<Theme>> = {
  base: () => import("./assets/base/theme.json"),
  fairy_tale: () => import("./assets/fairy_tale/theme.json"),
  flower_garden: () => import("./assets/flower_garden/theme.json"),
  hacienda: () => import("./assets/hacienda/theme.json"),
  magical_night: () => import("./assets/magical_night/theme.json"),
  tropical: () => import("./assets/tropical/theme.json"),
};

// Service to load and apply a theme dynamically
export const loadTheme = async (themeName: string): Promise<void> => {
  try {
    const loadThemeFile = themeMap[themeName];

    if (!loadThemeFile) {
      console.error(`Theme "${themeName}" not found.`);
      return;
    }

    // const theme: Theme = await loadThemeFile();
    const themeModule = (await loadThemeFile()) as unknown as ThemeModule;
    const theme: Theme = themeModule.default;

    if (!validateTheme(theme)) {
      console.error(`Theme "${themeName}" is invalid.`);
      return;
    }

    // Update CSS variables
    applyTheme(theme);
  } catch (error) {
    console.error("Failed to load theme", error);
  }
};

// Function to update CSS variables dynamically
const applyTheme = (theme: Theme): void => {
  document.documentElement.style.setProperty(
    "--color-primary",
    theme.colors.primary
  );
  document.documentElement.style.setProperty(
    "--color-secondary",
    theme.colors.secondary
  );
  document.documentElement.style.setProperty(
    "--color-background",
    theme.colors.background.body
  );
  document.documentElement.style.setProperty(
    "--color-text-primary",
    theme.colors.text.primary
  );
  document.documentElement.style.setProperty(
    "--color-text-secondary",
    theme.colors.text.secondary
  );
  document.documentElement.style.setProperty(
    "--color-link",
    theme.colors.text.link
  );
  document.documentElement.style.setProperty(
    "--color-link-hover",
    theme.colors.text.linkHover
  );
  document.documentElement.style.setProperty(
    "--color-border-default",
    theme.colors.border.default
  );
  document.documentElement.style.setProperty(
    "--color-border-focus",
    theme.colors.border.focus
  );
  document.documentElement.style.setProperty(
    "--color-button-bg-primary",
    theme.colors.button.background.primary
  );
  document.documentElement.style.setProperty(
    "--color-button-bg-secondary",
    theme.colors.button.background.secondary
  );
  document.documentElement.style.setProperty(
    "--color-button-bg-hover",
    theme.colors.button.background.hover
  );
  document.documentElement.style.setProperty(
    "--color-button-text-primary",
    theme.colors.button.text.primary
  );
  document.documentElement.style.setProperty(
    "--color-button-text-secondary",
    theme.colors.button.text.secondary
  );
};
